import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { checkHealth, generateImage, streamChat } from './lib/api';
import { messageImage, messageText } from './lib/chat-content';
import { prepareImage } from './lib/image';
import { createId } from './lib/storage';
import { useConversations } from './hooks/useConversations';
import { useModels } from './hooks/useModels';
import { DEFAULT_IMAGE_MODEL, type ChatMessage, type ImageGenerationOptions, type MessageContent, type PendingImage } from './types/chat';
import { AuthPage } from './components/AuthPage';
import { AdminPage } from './components/AdminPage';
import { Composer } from './components/Composer';
import { ConversationView } from './components/ConversationView';
import { EmptyState } from './components/EmptyState';
import { ResetDialog } from './components/ResetDialog';
import { Sidebar } from './components/Sidebar';
import { Toast } from './components/Toast';
import { TopBar } from './components/TopBar';

function newMessage(
  role: ChatMessage['role'],
  content: MessageContent,
  extra: Partial<ChatMessage> = {},
): ChatMessage {
  return { id: createId(), role, content, createdAt: Date.now(), status: 'complete', ...extra };
}

function outputBudget(alias: string): number {
  if (alias === 'fast') return 8192;
  if (alias === 'powerful') return 4096;
  return 3000;
}

function ChatApp({ onNavigate }: { onNavigate: (path: '/login' | '/register' | '/admin') => void }) {
  const conversations = useConversations();
  const modelState = useModels();
  const [draft, setDraft] = useState('');
  const [pendingImage, setPendingImage] = useState<PendingImage | null>(null);
  const [preparingImage, setPreparingImage] = useState(false);
  const [imageMode, setImageMode] = useState(false);
  const [imageOptions, setImageOptions] = useState<ImageGenerationOptions>({
    size: '1024x1024', quality: 'high', outputFormat: 'png',
  });
  const [sending, setSending] = useState(false);
  const [online, setOnline] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);
  const [resetOpen, setResetOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; error?: boolean }>({ message: '' });
  const [followSignal, setFollowSignal] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const toastTimerRef = useRef<number | null>(null);

  const active = conversations.active;
  const messages = active?.messages ?? [];

  const showToast = useCallback((message: string, error = false) => {
    if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current);
    setToast({ message, error });
    toastTimerRef.current = window.setTimeout(() => {
      setToast({ message: '' });
      toastTimerRef.current = null;
    }, 2200);
  }, []);

  useEffect(() => () => {
    abortRef.current?.abort();
    if (toastTimerRef.current !== null) window.clearTimeout(toastTimerRef.current);
  }, []);

  useEffect(() => {
    let activePoll = true;
    const poll = async () => {
      const healthy = await checkHealth();
      if (activePoll) setOnline(healthy);
    };
    void poll();
    const timer = window.setInterval(poll, 30_000);
    return () => {
      activePoll = false;
      window.clearInterval(timer);
    };
  }, []);

  const replaceAssistant = useCallback((conversationId: string, assistantId: string, update: (message: ChatMessage) => ChatMessage) => {
    conversations.setMessages(conversationId, (current) =>
      current.map((message) => (message.id === assistantId ? update(message) : message)),
    );
  }, [conversations]);

  const runStream = useCallback(async (
    conversationId: string,
    baseMessages: ChatMessage[],
    assistant: ChatMessage,
    modelAlias: string,
    retry = false,
  ): Promise<void> => {
    const controller = new AbortController();
    abortRef.current = controller;
    let answer = '';
    let displayedContent = '';
    let reasoning = '';

    try {
      answer = await streamChat({
        model: modelAlias,
        messages: baseMessages,
        signal: controller.signal,
        maxTokens: retry ? 8192 : outputBudget(modelAlias),
        onDelta: (delta) => {
          if (delta.content) displayedContent += delta.content;
          if (delta.reasoning) reasoning += delta.reasoning;
          replaceAssistant(conversationId, assistant.id, (message) => ({
            ...message,
            content: displayedContent,
            reasoning: reasoning || undefined,
            status: 'streaming',
            error: undefined,
          }));
        },
      });

      if (!answer.trim() && !retry) {
        await runStream(conversationId, baseMessages, assistant, modelAlias, true);
        return;
      }
      if (!answer.trim()) throw new Error('Model selesai tanpa teks jawaban. Silakan coba lagi.');
      replaceAssistant(conversationId, assistant.id, (message) => ({
        ...message,
        content: answer,
        reasoning: undefined,
        status: 'complete',
      }));
    } catch (reason) {
      if (controller.signal.aborted) {
        replaceAssistant(conversationId, assistant.id, (message) => ({
          ...message,
          content: displayedContent || message.content,
          reasoning: displayedContent ? undefined : message.reasoning,
          status: displayedContent ? 'stopped' : 'complete',
        }));
      } else {
        const error = reason instanceof Error ? reason.message : 'Gagal menghubungi server.';
        replaceAssistant(conversationId, assistant.id, (message) => ({ ...message, status: 'error', error }));
      }
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setSending(false);
    }
  }, [replaceAssistant]);

  const runImageGeneration = useCallback((conversationId: string, assistant: ChatMessage, prompt: string, options: ImageGenerationOptions) => {
    void generateImage(prompt, options)
      .then((url) => replaceAssistant(conversationId, assistant.id, (message) => ({
        ...message,
        content: [{ type: 'image_url', image_url: { url, name: 'Gambar AI' } }],
        status: 'complete',
      })))
      .catch((reason) => replaceAssistant(conversationId, assistant.id, (message) => ({
        ...message, status: 'error', error: reason instanceof Error ? reason.message : 'Gagal membuat gambar.',
      })))
      .finally(() => setSending(false));
  }, [replaceAssistant]);

  const submit = useCallback(() => {
    const text = draft.trim();
    if ((!text && !pendingImage) || sending || !active || (!imageMode && !modelState.selected)) return;

    if (imageMode) {
      const user = newMessage('user', text);
      const assistant = newMessage('assistant', '', {
        status: 'thinking', modelAlias: DEFAULT_IMAGE_MODEL, modelName: 'Gambar AI',
        imageGeneration: { prompt: text, options: imageOptions },
      });
      conversations.setMessages(active.id, [...active.messages, user, assistant]);
      setDraft('');
      setSending(true);
      setFollowSignal((value) => value + 1);
      runImageGeneration(active.id, assistant, text, imageOptions);
      return;
    }

    const requestModel = modelState.selected;
    if (!requestModel) return;
    const content: MessageContent = pendingImage
      ? [
          ...(text ? [{ type: 'text' as const, text }] : []),
          { type: 'image_url' as const, image_url: { url: pendingImage.dataUrl, name: pendingImage.name, size: pendingImage.size } },
        ]
      : text;
    const user = newMessage('user', content);
    const assistant = newMessage('assistant', '', {
      status: 'thinking',
      modelAlias: requestModel.alias,
      modelName: requestModel.name,
    });
    const baseMessages = [...active.messages, user];

    conversations.setMessages(active.id, [...baseMessages, assistant]);
    setDraft('');
    setPendingImage(null);
    setSending(true);
    setFollowSignal((value) => value + 1);
    void runStream(active.id, baseMessages, assistant, requestModel.alias);
  }, [active, conversations, draft, imageMode, imageOptions, modelState.selected, pendingImage, runImageGeneration, runStream, sending]);

  const stop = useCallback(() => abortRef.current?.abort(), []);

  const regenerate = useCallback((message: ChatMessage) => {
    if (sending || !active) return;
    const index = active.messages.findIndex((item) => item.id === message.id);
    if (index < 0) return;
    const baseMessages = active.messages.slice(0, index);

    if (message.imageGeneration) {
      const { prompt, options } = message.imageGeneration;
      const assistant = newMessage('assistant', '', {
        status: 'thinking', modelAlias: DEFAULT_IMAGE_MODEL, modelName: 'Gambar AI',
        imageGeneration: { prompt, options },
      });
      conversations.setMessages(active.id, [...baseMessages, assistant]);
      setSending(true);
      setFollowSignal((value) => value + 1);
      runImageGeneration(active.id, assistant, prompt, options);
      return;
    }

    if (!modelState.selected) return;
    const assistant = newMessage('assistant', '', {
      status: 'thinking', modelAlias: modelState.selected.alias, modelName: modelState.selected.name,
    });
    conversations.setMessages(active.id, [...baseMessages, assistant]);
    setSending(true);
    setFollowSignal((value) => value + 1);
    void runStream(active.id, baseMessages, assistant, modelState.selected.alias);
  }, [active, conversations, modelState.selected, runImageGeneration, runStream, sending]);

  const edit = useCallback((message: ChatMessage) => {
    if (sending || !active) return;
    const index = active.messages.findIndex((item) => item.id === message.id);
    if (index < 0) return;
    conversations.setMessages(active.id, active.messages.slice(0, index));
    setDraft(messageText(message.content));
    const image = messageImage(message.content);
    setPendingImage(image ? { dataUrl: image.url, name: image.name || 'gambar.jpg', size: image.size || 0 } : null);
  }, [active, conversations, sending]);

  const attach = useCallback(async (file: File) => {
    setPreparingImage(true);
    try {
      setPendingImage(await prepareImage(file));
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : 'Gagal memproses gambar.', true);
    } finally {
      setPreparingImage(false);
    }
  }, [showToast]);

  const copy = useCallback(async (message: ChatMessage) => {
    await navigator.clipboard.writeText(messageText(message.content));
    showToast('Jawaban disalin');
  }, [showToast]);

  const share = useCallback(async () => {
    const text = messages.map((message) => `${message.role === 'user' ? 'Anda' : message.modelName || 'AI'}:\n${messageText(message.content)}`).join('\n\n');
    try {
      if (navigator.share) await navigator.share({ title: active.title, text });
      else {
        await navigator.clipboard.writeText(text);
        showToast('Percakapan disalin');
      }
    } catch (reason) {
      if (reason instanceof DOMException && reason.name === 'AbortError') return;
      showToast('Tidak dapat membagikan percakapan', true);
    }
  }, [active.title, messages, showToast]);

  const activeTitle = useMemo(() => active?.title || 'Percakapan baru', [active?.title]);

  return (
    <div className="flex h-dvh min-h-0 bg-canvas text-primary">
      <Sidebar
        conversations={conversations.conversations}
        activeId={conversations.activeId}
        online={online}
        mobileOpen={drawerOpen}
        desktopOpen={desktopSidebarOpen}
        hasMessages={messages.length > 0}
        onCloseMobile={() => setDrawerOpen(false)}
        onCloseDesktop={() => setDesktopSidebarOpen(false)}
        onCreate={() => {
          stop();
          conversations.createConversation();
          setDraft('');
          setPendingImage(null);
        }}
        onSelect={(id) => {
          stop();
          conversations.selectConversation(id);
          setDraft('');
          setPendingImage(null);
        }}
        onReset={() => {
          setDrawerOpen(false);
          setResetOpen(true);
        }}
        onLogin={() => onNavigate('/login')}
        onRegister={() => onNavigate('/register')}
        onAdmin={() => onNavigate('/admin')}
      />

      <main className="flex min-w-0 flex-1 flex-col bg-canvas-subtle">
        <TopBar
          title={activeTitle}
          models={modelState.models}
          selected={modelState.selected}
          selectedAlias={modelState.selectedModel}
          modelLoading={modelState.loading}
          modelError={modelState.error}
          hasMessages={messages.length > 0}
          desktopSidebarOpen={desktopSidebarOpen}
          onOpenMenu={() => {
            setDrawerOpen(true);
            setDesktopSidebarOpen(true);
          }}
          onSelectModel={modelState.setSelectedModel}
          onShare={() => void share()}
          onReset={() => setResetOpen(true)}
        />

        {messages.length ? (
          <ConversationView messages={messages} onCopy={copy} onEdit={edit} onRegenerate={regenerate} followSignal={followSignal} />
        ) : (
          <EmptyState onSuggestion={setDraft} />
        )}

        <div className="shrink-0 px-2 pb-[max(8px,env(safe-area-inset-bottom))] pt-3 sm:px-5 sm:pb-5 lg:px-8">
          <div className="mx-auto flex max-w-[800px] justify-center">
            <Composer
              value={draft}
              image={pendingImage}
              model={modelState.selected}
              sending={sending}
              preparingImage={preparingImage}
              imageMode={imageMode}
              imageOptions={imageOptions}
              disabled={!online || (!imageMode && (modelState.loading || !modelState.selected))}
              onChange={setDraft}
              onSubmit={submit}
              onStop={stop}
              onFile={(file) => void attach(file)}
              onRemoveImage={() => setPendingImage(null)}
              onToggleImageMode={() => {
                if (sending) return;
                setImageMode((enabled) => !enabled);
                setPendingImage(null);
              }}
              onImageOptionsChange={setImageOptions}
            />
          </div>
        </div>
      </main>

      <ResetDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        onConfirm={() => {
          stop();
          conversations.resetConversation(active.id);
          setResetOpen(false);
          setDraft('');
          setPendingImage(null);
        }}
      />
      <Toast message={toast.message} error={toast.error} />
    </div>
  );
}

type AppRoute = '/' | '/login' | '/register' | '/admin';

function routeFromPath(pathname: string): AppRoute {
  if (pathname === '/login' || pathname === '/register' || pathname === '/admin') return pathname;
  return '/';
}

export default function App() {
  const [route, setRoute] = useState<AppRoute>(() => routeFromPath(window.location.pathname));

  const navigate = useCallback((path: AppRoute) => {
    window.history.pushState({}, '', path);
    setRoute(path);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    const onPopState = () => setRoute(routeFromPath(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    document.title = route === '/login'
      ? 'Masuk · Multi-Model AI'
      : route === '/register'
        ? 'Buat Akun · Multi-Model AI'
        : 'Multi-Model AI';
  }, [route]);

  if (route === '/login' || route === '/register') {
    return (
      <AuthPage
        key={route}
        mode={route === '/register' ? 'register' : 'login'}
        onNavigate={navigate}
      />
    );
  }

  if (route === '/admin') return <AdminPage onNavigate={navigate} />;

  return <ChatApp onNavigate={navigate} />;
}
